<template>
  <div>
    <div class="title-div text-center">
      <h2 class="mb-1">All MRC Scale scores and slopes</h2>
      <p class="mb-1">
        Fitted slopes (dashed) and connected raw scores (lines and dots)
      </p>
    </div>
    <transition :name="showGraph ? 'fade1' : 'fade2'" mode="out-in">
      <div
        v-if="requestError"
        class="overlay-div d-flex align-center justify-center"
      >
        <p class="mb-0 px-5 text-center">
          There seems to be a problem with our service. Please try again later.
          If the error persists, please notify us.
        </p>
      </div>
      <div
        v-else-if="fewPatients"
        class="overlay-div d-flex align-center justify-center"
      >
        <p class="mb-0 px-5 text-center">
          Cannot display fewer than 2 patients. Please expand your filtering
          criteria.
        </p>
      </div>
      <line-chart
        v-else-if="showGraph"
        :chartData="chartData"
        :options="options"
        class="graph"
        id="MainChart"
      ></line-chart>
      <div v-else class="loading-div d-flex align-center justify-center">
        <v-progress-circular
          indeterminate
          size="80"
          color="accent"
        ></v-progress-circular>
      </div>
    </transition>
  </div>
</template>

<style scoped>
.title-div p {
  font-size: 12px;
}

.loading-div {
  height: 400px;
}

.overlay-div {
  height: 400px;
  background-color: darkgray;
  border-radius: 15px;
}

.graph {
  max-height: 400px;
}

/* Transitions */
.fade1-enter-active,
.fade1-leave-active {
  opacity: 1;
  transition: all 0.25s ease;
}

.fade1-enter,
.fade1-leave-to,
.fade2-enter,
.fade2-leave-to {
  opacity: 0;
}

.fade2-enter-active,
.fade2-leave-active {
  opacity: 1;
}
</style>

<script>
import LineChart from "@/components/charts/LineChart";
import distinctColors from "distinct-colors";
import { mapState } from "vuex";

let colorOptions = {
  chromaMin: 20,
  chromaMax: 90,
  lightMin: 20,
  lightMax: 90
};

const grayColor = "rgba(171, 171, 171, 0.2)";
const highlightColor = "#00838F";
const nonHighlightColor = "rgba(239, 83, 80, 0.2)";
const maxNumColors = 40;
const lineBorderWidth = 1;

const prepareLine = function(slope, intercept, color, colorModel) {
  // Prepare the two points that will define the line
  // Point 1 will be on the y axis
  let point1 = {
    x: 0,
    y: intercept
  };

  // Point 2 will be on the x axis
  let point2 = {
    x: -(intercept / slope),
    y: 0
  };

  let slopeData = [point1, point2];

  let line = {
    fill: false,
    lineTension: 0,
    borderColor: color,
    pointRadius: 0,
    pointHoverRadius: 0,
    data: slopeData,
    order: colorModel ? 0 : 1,
    borderWidth: lineBorderWidth,
    pointHoverBorderWidth: 2,
    borderDash: [5, 5]
  };

  return line;
};

export default {
  components: {
    LineChart
  },
  props: {
    patientData: {
      type: Array,
      default: () => []
    },
    fewPatients: {
      type: Boolean,
      default: false
    },
    highlightGroup: {
      type: Boolean,
      default: false
    },
    requestError: {
      type: Boolean,
      default: false
    }
  },
  data: () => ({
    datasets: [],
    showGraph: false
  }),
  computed: {
    ...mapState([
      "colorScores",
      "colorModel",
      "axisMax",
      "axisMaxAuto",
      "hideNoModel"
    ]),
    options() {
      return {
        responsive: true,
        maintainAspectRatio: false,
        animation: {
          duration: 0
        },
        tooltips: {
          enabled: true
        },
        legend: {
          display: false
        },
        scales: {
          xAxes: [
            {
              type: "linear",
              position: "bottom",
              ticks: {
                max: this.axisMaxAuto ? undefined : this.axisMax,
                min: 0
              },
              scaleLabel: {
                display: true,
                labelString: "Days"
              }
            }
          ],
          yAxes: [
            {
              scaleLabel: {
                display: true,
                labelString: "MRC score / 20"
              },
              ticks: {
                min: 0,
                max: 20
              }
            }
          ]
        }
      };
    },
    chartData() {
      return {
        datasets: this.datasets
      };
    }
  },
  methods: {
    prepareDatasets() {
      let datasets = [];

      // We will generate colors only when we are not highlighting patients
      let colors;
      if (!this.highlightGroup) {
        colors = this.generateColors(this.patientData.length);
      }

      this.patientData.forEach((patient, index) => {
        let currentColor;
        if (this.highlightGroup) {
          currentColor = highlightColor;
        } else {
          currentColor = colors[index].hex();
        }

        // -------------
        let scoresColor;
        if (this.colorScores) {
          if (this.highlightGroup && !patient.highlighted) {
            scoresColor = nonHighlightColor;
          } else {
            scoresColor = currentColor;
          }
        } else {
          scoresColor = grayColor;
        }

        let modelColor;
        if (this.colorModel) {
          if (this.highlightGroup && !patient.highlighted) {
            modelColor = nonHighlightColor;
          } else {
            modelColor = currentColor;
          }
        } else {
          modelColor = grayColor;
        }
        // --------

        // Prepare the patient assessments
        if (patient.modelFitted || !this.hideNoModel) {
          let assessments = patient.assessments.map(x => {
            return {
              x: x.days,
              y: x.mrc20
            };
          });

          let patientAssessments = {
            label: "Patient " + patient.no,
            fill: false,
            lineTension: 0,
            borderColor: scoresColor,
            pointRadius: 2,
            pointHoverRadius: 2,
            data: assessments,
            order: this.colorScores ? 0 : 1,
            borderWidth: lineBorderWidth,
            pointHoverBorderWidth: lineBorderWidth
          };

          datasets.push(patientAssessments);
        }

        // Prepare the slope only if the model was fitted
        if (patient.modelFitted) {
          // Calculate intercept from slope and one point
          let slope = patient.slope / 5; // To convert mrc100 to mrc20
          let point = patient.assessments[0];
          let x = point.days;
          let y = point.fitted;
          let intercept = (y - slope * x) / 5; // To convert mrc100 to mrc20

          let slopeLine = prepareLine(
            slope,
            intercept,
            modelColor,
            this.colorModel
          );

          datasets.push(slopeLine);
        }
      });

      this.datasets = datasets;
    },
    async rerenderChart() {
      this.showGraph = false;
      this.prepareDatasets();
      await this.$nextTick();
      this.showGraph = true;
    },
    generateColors(colorNumber) {
      // Only calculate up to a number of distinct colors
      if (colorNumber <= maxNumColors) {
        return distinctColors({ ...colorOptions, count: colorNumber });
      } else {
        // If more colors are requested, then
        // repeat the array
        let palette = distinctColors({ ...colorOptions, count: maxNumColors });
        let newPalette = new Array(colorNumber);

        for (var i = 0; i < colorNumber; i++) {
          let colorIndex = i % maxNumColors;
          newPalette[i] = palette[colorIndex];
        }
        return newPalette;
      }
    }
  },
  watch: {
    patientData: {
      immediate: true,
      handler(value) {
        if (value.length > 0) {
          this.prepareDatasets();
          this.showGraph = true;
        } else {
          this.showGraph = false;
        }
      }
    },
    colorModel: function() {
      this.rerenderChart();
    },
    colorScores: function() {
      this.rerenderChart();
    },
    axisMaxAuto: function() {
      this.rerenderChart();
    },
    axisMax: function() {
      this.rerenderChart();
    },
    hideNoModel: function() {
      this.rerenderChart();
    }
  }
};
</script>
